import { stringify } from 'querystring';
import React, { ComponentType, ReactNode, useEffect } from 'react';
import { Data } from '@catalogo/core-sku/src/types';
import { ApolloError } from '@apollo/client/errors';
import compose from 'lodash/fp/compose';
import { getIn } from 'seamless-immutable';
import { getCurrentSku } from '@catalogo/core-sku';
import { withMetrics } from '@catalogo/core-metrics';
import { hasCriticalErrors, has404OnPath } from '@catalogo/core-graphql-errors';
import { useProductPage } from '../hooks/product-page';
import { fmtProduct } from '../helpers/product-page';

interface WithProductPageProps {
  children: ReactNode;
  match: {
    params: {
      id: string;
    };
  };
  routeId: string;
  parsedQs: {
    sellerId: string;
    offerId: string;
  };
  history: {
    replace(url: string): void;
  };
  dispatchMetrics(): void;
}

export const fmtStatus = ({
  error,
  called,
  loading,
}: {
  error: ApolloError | undefined;
  called: boolean;
  loading: boolean;
}) => {
  const graphqlErrors = getIn(error, ['graphQLErrors']);
  const hasCriticalError = graphqlErrors && hasCriticalErrors(graphqlErrors, ['product']);
  const has404Error = graphqlErrors && has404OnPath(graphqlErrors, ['product']);

  if (loading || !called) {
    return 'loading';
  }

  if (has404Error) {
    return 'notFound';
  }

  if (hasCriticalError) {
    return 'error';
  }
};

export const withProductPage = (Component: ComponentType) => ({
  match,
  routeId,
  parsedQs,
  history,
  dispatchMetrics,
  ...props
}: WithProductPageProps) => {
  const productId = getIn(match, ['params', 'id']);

  const { data, error, called, loading, refetch, reviewsQueries, experiment } = useProductPage({
    path: `/produto/${productId}`,
    sellerId: getIn(parsedQs, ['sellerId'], ''),
    offerId: getIn(parsedQs, ['offerId'], ''),
    productId,
    routeId,
    dispatchMetrics,
  });

  const currentSku = getCurrentSku(data as Data, parsedQs);
  const status = fmtStatus({ loading, called, error });
  const componentProps = {
    ...props,
    status,
    refetch,
    productId,
    reviewsQueries,
    history,
    parsedQs,
    currentSku,
    breadcrumb: getIn(data, ['page', 'breadcrumb']) || [],
    product: fmtProduct(getIn(data, ['product'], {}), reviewsQueries),
    page: getIn(data, ['page'], {}),
    components: getIn(data, ['page', 'components'], {}),
    routeId,
    experiment,
  };

  useEffect(() => {
    const hasOffers = getIn(currentSku, ['offers', 'result'], []).length > 0;

    if (hasOffers) {
      const parameters = {
        ...parsedQs,
        ...currentSku?.qs,
      };

      history.replace(`?${stringify(parameters)}`);
    }
  }, []);

  return <Component {...componentProps} />;
};

export default compose(withMetrics, withProductPage);
